import { Alert, Box, Stack, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import {
  FORMS_FORMS,
  FORMS_SUBMISSIONS,
  FORMS_SUBMISSION_COLUMNS,
  FORMS_SUBMISSION_DETAIL,
  FORMS_SUBMISSION_EXPORT,
  FORMS_SUBMISSION_STATS,
} from "../../config/urls";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import BackButton from "../../common/back-button";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import CustomTabs from "../../common/custom-tabs";
import PageLoader from "../../common/page-loader";
import SideDrawer from "../../common/side-drawer";
import StatTile from "../../common/stat-tile";
import TableSearchInput from "../TableSearchInput";
import FormDataDestinations from "./FormDataDestinations";

const ROWS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const SOURCE_OPTIONS = [
  { label: "All sources", value: "" },
  { label: "Template", value: "TEMPLATE" },
  { label: "Inbox", value: "INBOX" },
  { label: "Workflow", value: "WORKFLOW" },
  { label: "Test sends", value: "TEST" },
];

const RANGE_OPTIONS = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "All time", value: 0 },
];

const ISSUE_OPTIONS = [
  { label: "All submissions", value: "" },
  { label: "Needs attention", value: "1" },
];

const CHOICE_TYPES = ["single_choice", "multiple_choice", "dropdown"];

const STATUS_DOT_COLORS = {
  PUBLISHED: "#1FA45A",
  DRAFT: "#B7791F",
  DEPRECATED: "#6A7284",
};

const sourceLabel = (value) =>
  SOURCE_OPTIONS.find((o) => o.value === value)?.label || value || "—";

const formatSubmitted = (value) => dayjs(value).format("D MMM, HH:mm");

// Stored answers hold option ids; show the option titles.
const displayAnswer = (column, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const titles = Object.fromEntries(
    (column?.options || []).map((o) => [o.id, o.title]),
  );
  if (Array.isArray(value)) {
    return value.length
      ? value.map((v) => titles[v] ?? v).join(", ")
      : "—";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(titles[value] ?? value);
};

const DETAIL_TABS = [
  { label: "Submissions", value: 0 },
  { label: "Data & destinations", value: 1 },
];

const FormSubmissions = (props) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;
  const formId = props.pageParams?.id;

  const [form, setForm] = useState(
    props.pageParams?.name
      ? { name: props.pageParams.name, status: props.pageParams.status }
      : null,
  );
  const [columns, setColumns] = useState([]);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  // The fields and the first page of rows both have to arrive before the table shows.
  const [columnsReady, setColumnsReady] = useState(false);
  const [firstPageReady, setFirstPageReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "error" });

  const [source, setSource] = useState("");
  const [rangeDays, setRangeDays] = useState(30);
  const [hasIssues, setHasIssues] = useState("");
  const [answerKey, setAnswerKey] = useState("");
  const [answerValue, setAnswerValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(props.pageParams?.tab ?? 0);
  const [dataControls, setDataControls] = useState({ dirty: false, saving: false });

  const choiceColumns = useMemo(
    () => columns.filter((c) => CHOICE_TYPES.includes(c.type) && c.options?.length),
    [columns],
  );
  const answerColumn = choiceColumns.find((c) => c.key === answerKey);

  const getHeaders = useCallback(async () => {
    const token = await tokenRef.current();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [tenant]);

  const filterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    if (rangeDays) {
      params.set("from", dayjs().subtract(rangeDays, "day").format("YYYY-MM-DD"));
    }
    if (answerKey && answerValue) {
      params.set("answer_key", answerKey);
      params.set("answer_value", answerValue);
    }
    if (hasIssues) params.set("has_issues", hasIssues);
    if (debouncedSearch) params.set("search", debouncedSearch);
    return params;
  }, [source, rangeDays, hasIssues, answerKey, answerValue, debouncedSearch]);

  const showError = (error, fallback) => {
    setAlert({ message: error?.message || fallback, severity: "error" });
  };

  const readError = async (response, fallback) => {
    if (response.status === 403) {
      return "Only admins and managers can view form submissions.";
    }
    const data = await response.json().catch(() => ({}));
    return data?.error || data?.detail || fallback;
  };

  // Debounced search.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Form header details and columns load once.
  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const headers = await getHeaders();
        const [formRes, colRes] = await Promise.all([
          fetch(`${FORMS_FORMS}${formId}/`, { headers }),
          fetch(FORMS_SUBMISSION_COLUMNS(formId), { headers }),
        ]);
        if (formRes.ok) setForm(await formRes.json());
        if (!colRes.ok) {
          throw new Error(await readError(colRes, "Failed to load form fields"));
        }
        setColumns(await colRes.json());
      } catch (error) {
        showError(error, "Failed to load form");
      } finally {
        setColumnsReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  useEffect(() => {
    if (!formId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const headers = await getHeaders();
        const params = filterParams();
        const listParams = new URLSearchParams(params);
        listParams.set("limit", ROWS_PER_PAGE);
        listParams.set("offset", page * ROWS_PER_PAGE);

        const [listRes, statsRes] = await Promise.all([
          fetch(`${FORMS_SUBMISSIONS(formId)}?${listParams}`, { headers }),
          fetch(`${FORMS_SUBMISSION_STATS(formId)}?${params}`, { headers }),
        ]);
        if (!listRes.ok) {
          throw new Error(await readError(listRes, "Failed to fetch submissions"));
        }
        const data = await listRes.json();
        if (cancelled) return;
        setRows(data.results);
        setCount(data.count);
        if (statsRes.ok) setStats(await statsRes.json());
      } catch (error) {
        if (!cancelled) showError(error, "Failed to fetch submissions");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFirstPageReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, page, filterParams]);

  const resetPage = () => setPage(0);

  const openDetail = async (row) => {
    setSelected({ ...row, loading: true });
    try {
      const res = await fetch(FORMS_SUBMISSION_DETAIL(formId, row.id), {
        headers: await getHeaders(),
      });
      if (!res.ok) throw new Error(await readError(res, "Failed to load submission"));
      setSelected(await res.json());
    } catch (error) {
      setSelected(null);
      showError(error, "Failed to load submission");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(
        `${FORMS_SUBMISSION_EXPORT(formId)}?${filterParams()}`,
        { headers: await getHeaders() },
      );
      if (response.status === 202) {
        const data = await response.json();
        setAlert({ message: data.message, severity: "success" });
        return;
      }
      if (!response.ok) throw new Error(await readError(response, "Export failed"));

      const disposition = response.headers.get("Content-Disposition") || "";
      const filename =
        /filename="?([^";]+)"?/.exec(disposition)?.[1] || "form-submissions.csv";
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error, "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const goBack = () => props.onNavigate("whatsapp_forms", { tab: 1 });

  const bodyText = { ...typography.bodyLarge, color: "#1D2330" };

  const tableColumns = [
    {
      id: "contact",
      label: "Contact",
      render: (row) => (
        <Stack>
          <Typography sx={bodyText}>{row.contact_name || "—"}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {row.phone_masked}
          </Typography>
        </Stack>
      ),
    },
    ...columns.map((col) => ({
      id: col.key,
      label: col.removed_in ? `${col.label} (removed in v${col.removed_in})` : col.label,
      render: (row) => (
        <Typography
          sx={{
            ...bodyText,
            maxWidth: 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={displayAnswer(col, row.answers?.[col.key])}
        >
          {displayAnswer(col, row.answers?.[col.key])}
        </Typography>
      ),
    })),
    {
      id: "issues",
      label: "Checks",
      render: (row) =>
        row.has_issues ? (
          <Typography sx={{ ...typography.bodySmall, color: "#b50000", fontWeight: 500 }}>
            {row.issues_count} {row.issues_count === 1 ? "issue" : "issues"}
          </Typography>
        ) : (
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>OK</Typography>
        ),
    },
    {
      id: "source",
      label: "Source",
      render: (row) => <Typography sx={bodyText}>{sourceLabel(row.source)}</Typography>,
    },
    {
      id: "submitted_at",
      label: "Submitted",
      render: (row) => (
        <Typography sx={bodyText}>{formatSubmitted(row.submitted_at)}</Typography>
      ),
    },
  ];

  const formatCount = (value) =>
    value === null || value === undefined ? "—" : Number(value).toLocaleString();
  const rate =
    stats?.submission_rate === null || stats?.submission_rate === undefined
      ? null
      : `${Math.round(stats.submission_rate * 100)}% of delivered`;

  if (!formId) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <BackButton onClick={goBack} />
        </Box>
        <Alert severity="info">Choose a form from View Forms to see its submissions.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box sx={{ flex: 1, minWidth: 0, px: 3, py: 2 }}>
        <Box sx={{ mb: 2 }}>
          <BackButton onClick={goBack} />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              <Box
                component="button"
                type="button"
                onClick={goBack}
                sx={{
                  ...typography.bodySmall,
                  background: "none",
                  border: 0,
                  p: 0,
                  cursor: "pointer",
                  color: ColorsConstants.primaryPurple100,
                }}
              >
                View Forms
              </Box>
              {" › "}
              {form?.name}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={typography.h5}>{form?.name || "Submissions"}</Typography>
              {form?.status && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    ...typography.bodySmall,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: STATUS_DOT_COLORS[form.status] || "#6A7284",
                    }}
                  />
                  {form.status.charAt(0) + form.status.slice(1).toLowerCase()}
                </Box>
              )}
            </Stack>
          </Box>
          {tab === 0 ? (
            <ButtonOne
              title={exporting ? "Exporting..." : "Export CSV"}
              onClick={handleExport}
              disabled={exporting || count === 0}
            />
          ) : (
            <ButtonOne
              title={dataControls.saving ? "Saving..." : "Save changes"}
              onClick={() => dataControls.save?.()}
              disabled={!dataControls.dirty || dataControls.saving}
            />
          )}
        </Box>

        {alert.message && (
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ message: "", severity: "error" })}
            sx={{ mb: 2, "& .MuiAlert-message": typography.bodyLarge }}
          >
            {alert.message}
          </Alert>
        )}

        <Box sx={{ mb: 2, width: { xs: "100%", md: "50%" } }}>
          <CustomTabs value={tab} onChange={(e, value) => setTab(value)} tabs={DETAIL_TABS} />
        </Box>

        {tab === 1 && (
          <FormDataDestinations formId={formId} onControlsChange={setDataControls} />
        )}

        {tab === 0 && !(columnsReady && firstPageReady) && <PageLoader />}

        {tab === 0 && columnsReady && firstPageReady && (
          <>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <StatTile value={formatCount(stats?.sent)} label="Sent" />
          <StatTile value={formatCount(stats?.delivered)} label="Delivered" />
          <StatTile value={formatCount(stats?.read)} label="Read" />
          <StatTile
            value={formatCount(stats?.submitted)}
            label={rate ? `Submitted · ${rate}` : "Submitted"}
          />
        </Stack>

        <Box
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              px: 2.25,
              py: 1.5,
              borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            }}
          >
            <AppDropdown
              menuItems={SOURCE_OPTIONS}
              selectedValue={source}
              onMenuSelect={(value) => {
                setSource(value);
                resetPage();
              }}
              endIcon={<ExpandMore />}
              sx={{ minWidth: 160, justifyContent: "space-between" }}
            >
              {sourceLabel(source) === "—" ? "All sources" : sourceLabel(source)}
            </AppDropdown>

            <AppDropdown
              menuItems={ISSUE_OPTIONS}
              selectedValue={hasIssues}
              onMenuSelect={(value) => {
                setHasIssues(value);
                resetPage();
              }}
              endIcon={<ExpandMore />}
              sx={{ minWidth: 170, justifyContent: "space-between" }}
            >
              {ISSUE_OPTIONS.find((o) => o.value === hasIssues)?.label}
            </AppDropdown>

            <AppDropdown
              menuItems={RANGE_OPTIONS}
              selectedValue={rangeDays}
              onMenuSelect={(value) => {
                setRangeDays(value);
                resetPage();
              }}
              endIcon={<ExpandMore />}
              sx={{ minWidth: 150, justifyContent: "space-between" }}
            >
              {RANGE_OPTIONS.find((o) => o.value === rangeDays)?.label}
            </AppDropdown>

            {choiceColumns.length > 0 && (
              <>
                <AppDropdown
                  menuItems={[
                    { label: "Any answer", value: "" },
                    ...choiceColumns.map((c) => ({ label: c.label, value: c.key })),
                  ]}
                  selectedValue={answerKey}
                  onMenuSelect={(value) => {
                    setAnswerKey(value);
                    setAnswerValue("");
                    resetPage();
                  }}
                  endIcon={<ExpandMore />}
                  sx={{ minWidth: 160, justifyContent: "space-between" }}
                >
                  {answerColumn ? answerColumn.label : "Filter by answer"}
                </AppDropdown>
                {answerColumn && (
                  <AppDropdown
                    menuItems={answerColumn.options.map((o) => ({
                      label: o.title,
                      value: o.id,
                    }))}
                    selectedValue={answerValue}
                    onMenuSelect={(value) => {
                      setAnswerValue(value);
                      resetPage();
                    }}
                    endIcon={<ExpandMore />}
                    sx={{ minWidth: 140, justifyContent: "space-between" }}
                  >
                    {answerColumn.options.find((o) => o.id === answerValue)?.title ||
                      "Choose a value"}
                  </AppDropdown>
                )}
              </>
            )}

            <Box sx={{ width: 240, maxWidth: "100%", ml: "auto" }}>
              <TableSearchInput
                searchQuery={searchQuery}
                onSearchChange={(e) => setSearchQuery(e.target.value.slice(0, 50))}
                tooltip="Search by contact name or full phone number"
                placeholder="Search name or phone"
              />
            </Box>
          </Box>

          {!loading && count === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
              <Typography sx={typography.h5}>No submissions yet</Typography>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
                Answers appear here when contacts complete the form.
              </Typography>
            </Stack>
          ) : (
            <AppTable
              columns={tableColumns}
              rows={rows}
              rowKey="id"
              loading={loading}
              maxHeight={500}
              onRowClick={openDetail}
              headCellSx={{
                ...typography.bodySmall,
                backgroundColor: dashboardCardBgColor,
                color: grayFontColor,
                padding: "9px 18px",
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              }}
              bodyCellSx={{ padding: "10px 18px" }}
              rowSx={() => ({
                backgroundColor: "#FFFFFF",
                "&:hover": { backgroundColor: dashboardCardBgColor },
                "& td": {
                  borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                  borderTop: "none",
                },
                "&:last-child td": { borderBottom: "none" },
              })}
              paperSx={{ border: "none", borderRadius: 0, boxShadow: "none", mb: 0 }}
              count={count}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
            />
          )}
        </Box>
          </>
        )}
      </Box>

      <SideDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected?.contact_name || "Submission"}
        subtitle={selected?.phone_masked}
      >
        {selected && (
          <Stack spacing={2}>
            <Box>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                Submitted
              </Typography>
              <Typography sx={bodyText}>{formatSubmitted(selected.submitted_at)}</Typography>
            </Box>
            <Box>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                Source
              </Typography>
              <Typography sx={bodyText}>{sourceLabel(selected.source)}</Typography>
            </Box>
            {selected.version_number && (
              <Box>
                <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                  Form version
                </Typography>
                <Typography sx={bodyText}>v{selected.version_number}</Typography>
              </Box>
            )}
            {selected.phone_mismatch && (
              <Alert severity="warning" sx={{ "& .MuiAlert-message": typography.bodySmall }}>
                This reply came from a different number than the one the form was sent to.
              </Alert>
            )}
            {(selected.answers_labeled || []).map((answer) => {
              const invalid = answer.status === "invalid";
              const cleanedText =
                answer.status === "valid" &&
                answer.cleaned !== null &&
                answer.cleaned !== undefined &&
                String(answer.cleaned) !== String(answer.value) &&
                typeof answer.cleaned !== "object"
                  ? String(answer.cleaned)
                  : "";
              return (
                <Box key={answer.key}>
                  <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                    {answer.label}
                  </Typography>
                  <Typography sx={{ ...bodyText, wordBreak: "break-word" }}>
                    {answer.display || "—"}
                  </Typography>
                  {invalid && (
                    <Typography sx={{ ...typography.bodySmall, color: "#b50000" }}>
                      {answer.reason || "This answer didn't pass the checks"}
                    </Typography>
                  )}
                  {cleanedText && (
                    <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                      Saved as: {cleanedText}
                    </Typography>
                  )}
                </Box>
              );
            })}
            {selected.loading && (
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                Loading answers...
              </Typography>
            )}
          </Stack>
        )}
      </SideDrawer>
    </Box>
  );
};

export default FormSubmissions;
